import { Dict, JSONCompatible } from "./typing";
import LZString from "lz-string";
import {
  APP_IS_RUNNING_LOCALLY,
  base64ToBlob,
  blobOrFileToDataURL,
  dataURLToBlob,
  FLASK_BASE_URL,
} from "./utils";
import { v4 as uuid } from "uuid";
import Bottleneck from "bottleneck";
import { extractTextInBrowser } from "./extractText";
import {
  clearMedia,
  deleteMedia,
  getMedia,
  listMedia,
  putMedia,
} from "./mediaStore";
import { requestStorageProtection } from "./storagePersistence";

// NOTE: call APP_IS_RUNNING_LOCALLY() where it is needed rather than caching it
// at module scope. cache.ts and utils.ts import each other, so evaluating it
// during module initialization throws
// "ReferenceError: Cannot access '_APP_IS_RUNNING_LOCALLY' before
// initialization" whenever the cycle is entered through utils.ts. The result is
// already memoized inside APP_IS_RUNNING_LOCALLY itself.

/**
 * Singleton JSON cache that functions like a local filesystem in a Python backend,
 * but where 'storageKeys' are used in place of filepaths.
 */
export default class StorageCache {
  // eslint-disable-next-line no-use-before-define
  private static instance: StorageCache;
  private data: Dict;

  private constructor() {
    // Initialize the singleton instance
    this.data = {};
  }

  /** Gets the storage cache. Initializes it if the singleton instance does not yet exist. */
  public static getInstance(): StorageCache {
    if (!StorageCache.instance) {
      StorageCache.instance = new StorageCache();
    }
    return StorageCache.instance;
  }

  private getCacheData(key: string): any {
    return this.data[key] ?? undefined;
  }

  public static get(key: string): any {
    return StorageCache.getInstance().getCacheData(key);
  }

  private getAllCacheData(filterFunc: (key: string) => boolean): Dict {
    const res: Dict = {};
    Object.keys(this.data)
      .filter(filterFunc)
      .forEach((key) => {
        res[key] = this.data[key];
      });
    return res;
  }

  public static getAllMatching(filterFunc: (key: string) => boolean): Dict {
    return StorageCache.getInstance().getAllCacheData(filterFunc);
  }

  private hasKey(key: string): boolean {
    return key in this.data;
  }

  public static has(key: string): boolean {
    return StorageCache.getInstance().hasKey(key);
  }

  private storeCacheData(key: string, _data: any): void {
    this.data[key] = _data;
  }

  public static store(key: string, data: any): void {
    StorageCache.getInstance().storeCacheData(key, data);
  }

  private clearCache(key?: string): void {
    if (key === undefined) this.data = {};
    else if (key in this.data) delete this.data[key];
  }

  /**
   * Clears data in the cache.
   * @param key Optional. A specific key to clear in the storage dict. If undefined, clears all data.
   */
  public static clear(key?: string): void {
    StorageCache.getInstance().clearCache(key);
    if (key === undefined) StringLookup.restoreFrom([]);
  }

  /**
   * Attempts to store the entire cache in localStorage.
   * Performs lz-string compression (https://pieroxy.net/blog/pages/lz-string/index.html)
   * before storing a JSON object in UTF encoding.
   *
   * Use loadFromLocalStorage to unpack the localStorage data.
   *
   * @param localStorageKey The key that will be used in localStorage (default='chainforge')
   * @param data Optional. JSON-compatible data to store. If undefined, will store the StorageCache's data. If defined, will only store the passed data.
   * @returns True if succeeded, false if failure (e.g., too big for localStorage).
   */
  public static saveToLocalStorage(
    localStorageKey = "chainforge",
    data?: Dict,
  ): boolean {
    data = data ?? StorageCache.getInstance().data;
    const compressed = LZString.compressToUTF16(JSON.stringify(data));
    try {
      localStorage.setItem(localStorageKey, compressed);
      return true;
    } catch (error) {
      if (
        error instanceof DOMException &&
        error.name === "QuotaExceededError"
      ) {
        // Handle the error when storage quota is exceeded
        console.warn("Storage quota exceeded");
      } else {
        // Handle other types of storage-related errors
        console.error(
          "Error storing data in localStorage:",
          (error as Error).message,
        );
      }
      return false;
    }
  }

  /**
   * Attempts to load a previously stored cache JSON from localStorage.
   * Performs lz-string decompression from UTF16 encoding.
   *
   * @param localStorageKey The key that will be used in localStorage (default='chainforge')
   * @param replaceStorageCacheWithLoadedData Whether the data in the StorageCache should be saved with the loaded data. Erases all current memory. Only set this to true if you are replacing the ChainForge flow state entirely.
   * @returns Loaded data if succeeded, undefined if failure (e.g., key not found).
   */
  public static loadFromLocalStorage(
    localStorageKey = "chainforge",
    replaceStorageCacheWithLoadedData = false,
  ): JSONCompatible | undefined {
    const compressed = localStorage.getItem(localStorageKey);
    if (!compressed) {
      console.error(
        `Could not find cache data in localStorage with key ${localStorageKey}.`,
      );
      return undefined;
    }
    try {
      const data = JSON.parse(LZString.decompressFromUTF16(compressed));
      if (replaceStorageCacheWithLoadedData) {
        // Replaces the current cache data with the loaded data
        StorageCache.getInstance().data = data;
        // Restores the current StringLookup table with the contents of the loaded data, if the __s key is present.
        if (data.__s) StringLookup.restoreFrom(data.__s);
        // Restores the current MediaLookup table with the contents of the loaded data, if the __media key is present.
        if (data.__media) MediaLookup.restoreFrom(data.__media);
      }
      console.log("loaded", data);
      return data;
    } catch (error) {
      console.error((error as Error).message);
      return undefined;
    }
  }
}

/** Global string intern table for efficient storage of repeated strings */
export class StringLookup {
  // eslint-disable-next-line no-use-before-define
  private static instance: StringLookup;
  private stringToIndex: Map<string, number> = new Map();
  private indexToString: string[] = [];

  /** Gets the string intern lookup table. Initializes it if the singleton instance does not yet exist. */
  public static getInstance(): StringLookup {
    if (!StringLookup.instance) {
      StringLookup.instance = new StringLookup();
    }
    return StringLookup.instance;
  }

  /** Adds a string to the table and returns its index */
  public static intern(str: string): number {
    const s = StringLookup.getInstance();
    const existing = s.stringToIndex.get(str);
    if (existing !== undefined) return existing; // Already interned

    // Add new string to the table
    const index = s.indexToString.length;
    s.indexToString.push(str);
    s.stringToIndex.set(str, index);

    // Save to cache
    StorageCache.store("__s", s.indexToString);

    return index;
  }

  // Overloaded signatures
  // This tells TypeScript that a number or string will always produce a string or undefined,
  // whereas any other type T will return the same type.
  public static get(index: number | string | undefined): string | undefined;
  public static get<T>(index: T): T;

  /**
   * Retrieves the string in the lookup table, given its index.
   * - **Note**: This function soft fails: if index is not a number, returns index unchanged.
   */
  public static get<T>(index: T | number): T | string {
    if (typeof index !== "number") return index;
    const s = StringLookup.getInstance();
    return s.indexToString[index]; // O(1) lookup
  }

  /**
   * Transforms a Dict by interning all strings encountered, up to 1 level of depth,
   * and returning the modified Dict with the strings as hash indexes instead.
   *
   * NOTE: This ignores recursing into any key "llm" that has a dict component.
   */
  public static internDict(
    d: Dict,
    inplace?: boolean,
    depth = 1,
    ignoreKey = ["llm", "uid", "eval_res"],
  ): Dict {
    const newDict = inplace ? d : ({} as Dict);
    const entries = Object.entries(d);

    for (const [key, value] of entries) {
      if (ignoreKey.includes(key)) {
        // Keep the ignored key the same
        if (!inplace) newDict[key] = value;
        continue;
      }
      if (typeof value === "string") {
        newDict[key] = StringLookup.intern(value);
      } else if (
        Array.isArray(value) &&
        value.every((v) => typeof v === "string")
      ) {
        newDict[key] = value.map((v) => StringLookup.intern(v));
      } else if (depth > 0 && typeof value === "object" && value !== null) {
        newDict[key] = StringLookup.internDict(
          value as Dict,
          inplace,
          depth - 1,
        );
      } else {
        if (!inplace) newDict[key] = value;
      }
    }

    return newDict as Map<string, unknown>;
  }

  /**
   * Treats all numberic values in the dictionary as hashes, and maps them to strings.
   * Leaves the rest of the dict unchanged. (Only operates 1 level deep.)
   * @param d The dictionary to operate over
   */
  public static concretizeDict<T>(
    d: Dict<T | number>,
    inplace = false,
    depth = 1,
    ignoreKey = ["llm", "uid", "eval_res"],
  ): Dict<T | string> {
    const newDict = inplace ? d : ({} as Dict);
    const entries = Object.entries(d);
    for (const [key, value] of entries) {
      const ignore = ignoreKey.includes(key);
      // A number here is *usually* an index into the intern table, but it can
      // also be genuine numeric data (a score, a rank, a year) -- the two share
      // one value space. When the lookup misses, keep the number rather than
      // replacing it with undefined: doing so silently deleted the value, since
      // callers JSON round-trip these dicts and JSON drops undefined.
      if (!ignore && typeof value === "number")
        newDict[key] = StringLookup.get(value) ?? value;
      else if (
        !ignore &&
        Array.isArray(value) &&
        value.every((v) => typeof v === "number")
      )
        newDict[key] = value.map((v) => StringLookup.get(v) ?? v);
      else if (
        !ignore &&
        depth > 0 &&
        typeof value === "object" &&
        value !== null
      ) {
        newDict[key] = StringLookup.concretizeDict(
          value as Dict<unknown>,
          false,
          0,
        );
      } else if (!inplace) newDict[key] = value;
    }
    return newDict;
  }

  public static restoreFrom(savedIndexToString?: string[]): void {
    const s = StringLookup.getInstance();
    s.stringToIndex = new Map<string, number>();
    if (savedIndexToString === undefined || savedIndexToString.length === 0) {
      // Reset
      s.indexToString = [];
      return;
    } else if (!Array.isArray(savedIndexToString)) {
      // Reset, but warn user
      console.error(
        "String lookup table could not be loaded: data.__s is not an array.",
      );
      s.indexToString = [];
      return;
    }

    // Recreate from the index array
    s.indexToString = savedIndexToString;
    savedIndexToString.forEach((v, i) => {
      s.stringToIndex.set(v, i);
    });
  }

  /** Serializes interned strings and their mappings */
  public static toJSON() {
    const s = StringLookup.getInstance();
    return s.indexToString;
  }

  /** Restores from JSON */
  static fromJSON(data: { dictionary: string[] }) {
    const table = new StringLookup();
    table.indexToString = data.dictionary;
    table.stringToIndex = new Map(data.dictionary.map((str, i) => [str, i]));
    StringLookup.instance = table;
  }
}

/** Global media lookup table for efficient storage and retrieval of media (images, PDFs, etc) with the Flask backend. */
export class MediaLookup {
  // eslint-disable-next-line no-use-before-define
  private static instance: MediaLookup;

  /**
   * All media UIDs that are used in the currently loaded flow.
   * This is used to keep track of the currently loaded media,
   * and export all the relevant media for the flow when the user clicks "Export".
   */
  private mediaUIDs = new Set<string>();

  // Use a cache if running locally, otherwise use the backend
  private cache: Dict<Blob> = {};

  /**
   * Sizes of every browser-stored file, whether or not its bytes are currently
   * in `cache`.
   *
   * Files are persisted to IndexedDB, and loaded back on demand rather than all
   * at once, so `cache` holds only a subset. This index is what lets us report
   * usage and enforce the budget without pulling every Blob into memory.
   */
  private sizes: Map<string, number> = new Map();

  // Temporary in-memory cache with size limit
  private tempCache: {
    items: Map<string, Blob>;
    size: number;
    accessOrder: string[];
  } = {
    items: new Map(),
    size: 0,
    accessOrder: [],
  };

  // Maximum size of the temporary cache in bytes (50MB)
  private readonly MAX_TEMP_CACHE_SIZE = 50 * 1024 * 1024;

  /**
   * Browser-only limits.
   *
   * When there's no Flask backend, `cache` is the *authoritative* store for
   * uploaded files -- not a read-through cache -- so it cannot evict like
   * `tempCache` does: dropping a blob would break any node referencing its
   * uid. We therefore refuse uploads past a budget instead, with an error the
   * UI can show.
   *
   * The budget is sized for export, which is now the binding constraint:
   * exporting in browser mode base64-encodes every blob into one .cforge JSON
   * string, inflating it by 4/3. V8 caps a single string at ~512M chars, so
   * roughly 400MB of files would throw "RangeError: Invalid string length".
   * 250MB keeps the worst case near 65% of that ceiling.
   *
   * Files themselves live in IndexedDB (see ./mediaStore), so this is no longer
   * limited by how much can be held in memory.
   */
  private readonly MAX_BROWSER_FILE_BYTES = 50 * 1024 * 1024; // per file
  private readonly MAX_BROWSER_CACHE_BYTES = 250 * 1024 * 1024; // all files

  /** Total bytes currently held in the browser-mode store. */
  private browserCacheBytes(): number {
    let total = 0;
    for (const size of this.sizes.values()) total += size;
    return total;
  }

  /**
   * Throws a user-facing error if caching `blob` would exceed the browser
   * budget. No-op when a backend is available, where files go to disk.
   */
  private assertBrowserCacheHasRoom(blob: Blob, name?: string): void {
    const mb = (bytes: number) => `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    const label = name ? `"${name}"` : "This file";

    if (blob.size > this.MAX_BROWSER_FILE_BYTES)
      throw new Error(
        `${label} is ${mb(blob.size)}, over the ${mb(this.MAX_BROWSER_FILE_BYTES)} ` +
          `per-file limit for running ChainForge without a local server. ` +
          `Run ChainForge locally to work with larger files.`,
      );

    const used = this.browserCacheBytes();
    if (used + blob.size > this.MAX_BROWSER_CACHE_BYTES)
      throw new Error(
        `Adding ${label} (${mb(blob.size)}) would exceed the ${mb(this.MAX_BROWSER_CACHE_BYTES)} ` +
          `total limit for files held in the browser (${mb(used)} already in use). ` +
          `Remove some files, or run ChainForge locally.`,
      );
  }

  // Concurrency caps for requests to the local Flask server. There is no
  // minimum spacing between requests: a fixed gap put a floor on throughput
  // (50ms per lookup meant ~25s to fill a 500-image table) without protecting
  // the server any more than the concurrency cap already does.
  private static uploadLimiter = new Bottleneck({
    maxConcurrent: 4,
  });

  // Browsers allow 6 concurrent HTTP/1.1 connections per host; more would queue anyway.
  private static lookupLimiter = new Bottleneck({
    maxConcurrent: 6,
  });

  /**
   * Object URLs handed out by acquireUrl, shared per uid and reference counted.
   * An object URL keeps its Blob alive until revoked, so each must be revoked
   * once nothing displays it.
   */
  private objectUrls: Map<string, { url: string; refs: number }> = new Map();

  /** Incremented by clear(), so async work can tell a clear happened meanwhile. */
  private clearCount = 0;

  private revokeObjectUrl(uid: string): void {
    const entry = this.objectUrls.get(uid);
    if (!entry) return;
    URL.revokeObjectURL(entry.url);
    this.objectUrls.delete(uid);
  }

  private constructor() {
    // Initialize the singleton instance
    this.mediaUIDs = new Set<string>();
    this.cache = {};
    this.tempCache = {
      items: new Map(),
      size: 0,
      accessOrder: [],
    };
  }

  /**
   * Adds a blob to the temporary cache with size management.
   * Removes least recently used items if needed to stay under size limit.
   * @param uid The UID of the media
   * @param blob The blob to cache
   */
  private addToTempCache(uid: string, blob: Blob): void {
    // Skip if blob is too large on its own
    if (blob.size > this.MAX_TEMP_CACHE_SIZE) {
      return;
    }

    // Check if already in cache
    if (this.tempCache.items.has(uid)) {
      // Update access order (move to end)
      this.tempCache.accessOrder = this.tempCache.accessOrder.filter(
        (id) => id !== uid,
      );
      this.tempCache.accessOrder.push(uid);
      return;
    }

    // Make room if needed
    while (
      this.tempCache.size + blob.size > this.MAX_TEMP_CACHE_SIZE &&
      this.tempCache.accessOrder.length > 0
    ) {
      const oldestUid = this.tempCache.accessOrder.shift();
      if (oldestUid === undefined) break; // Safety check. This should not happen, but just in case.
      const oldestBlob = this.tempCache.items.get(oldestUid);
      if (oldestBlob) this.tempCache.size -= oldestBlob.size;
      this.tempCache.items.delete(oldestUid);
    }

    // Add the new blob
    this.tempCache.items.set(uid, blob);
    this.tempCache.size += blob.size;
    this.tempCache.accessOrder.push(uid);
  }

  /**
   * Gets a blob from the temporary cache and updates its access order
   * @param uid The UID of the media
   * @returns The cached blob or undefined if not found
   */
  private getFromTempCache(uid: string): Blob | undefined {
    const blob = this.tempCache.items.get(uid);
    if (blob) {
      // Update access order (move to end)
      this.tempCache.accessOrder = this.tempCache.accessOrder.filter(
        (id) => id !== uid,
      );
      this.tempCache.accessOrder.push(uid);
    }
    return blob;
  }

  /**
   * Records which media uids are in play, in the StorageCache.
   *
   * Only the uid list is stored. Blobs are deliberately left out: the
   * StorageCache is persisted with JSON.stringify, and `JSON.stringify(blob)`
   * is `{}` -- so storing them wrote a useless placeholder per file and, on
   * restore, installed those placeholders as though they were real Blobs.
   *
   * Blob bytes are held in memory for the session, and serialized properly
   * (as data URLs) by `toJSON()` when a flow is exported.
   */
  public saveStateToStorageCache(): void {
    StorageCache.store("__media", {
      mediaUIDs: Array.from(this.mediaUIDs),
    });
  }

  /**
   * Restores the set of known media uids from the StorageCache.
   *
   * Note that this recovers uids only, not file contents -- see
   * saveStateToStorageCache. Callers should treat a uid with no cached blob as
   * "referenced but unavailable" rather than assuming the bytes are present.
   */
  public restoreStateFromStorageCache(): boolean {
    const savedState = StorageCache.get("__media");
    if (savedState && Array.isArray(savedState.mediaUIDs)) {
      this.mediaUIDs = new Set(savedState.mediaUIDs);
      return true;
    }
    return false;
  }

  /**
   * Reloads the index of persisted files, without loading their contents.
   *
   * Call once at startup. Until this runs, files uploaded in a previous session
   * are still readable through get() -- which falls back to IndexedDB per uid --
   * but the reported usage and uid set would not include them.
   *
   * @returns How many persisted files were found.
   */
  public static async hydrateFromIndexedDB(): Promise<number> {
    const mediaLookup = MediaLookup.getInstance();
    const clearsBefore = mediaLookup.clearCount;
    const stored = await listMedia();

    // A clear() while the listing was in flight (e.g. the starter
    // flow replacing media at startup) makes the listing stale: applying it
    // would re-register deleted files, whose sizes would then count against the
    // budget with no bytes behind them.
    if (mediaLookup.clearCount !== clearsBefore) return 0;

    for (const { uid, size } of stored) {
      mediaLookup.mediaUIDs.add(uid);
      // Don't clobber a size already known from this session's own uploads.
      if (!mediaLookup.sizes.has(uid)) mediaLookup.sizes.set(uid, size);
    }

    if (stored.length > 0) mediaLookup.saveStateToStorageCache();
    return stored.length;
  }

  /**
   * How much file data the browser is currently holding, for display in the UI.
   */
  public static storageUsage(): {
    files: number;
    bytes: number;
    limitBytes: number;
    fileLimitBytes: number;
  } {
    const mediaLookup = MediaLookup.getInstance();
    return {
      files: mediaLookup.sizes.size,
      bytes: mediaLookup.browserCacheBytes(),
      limitBytes: mediaLookup.MAX_BROWSER_CACHE_BYTES,
      fileLimitBytes: mediaLookup.MAX_BROWSER_FILE_BYTES,
    };
  }

  /**
   * Adds a UID to the media UIDs set and optionally adds a Blob to the cache.
   * Handles saving the state to localStorage.
   * @param uid The UID of the media
   * @param blob Optional. A Blob or File object to cache
   */
  private add(uid: string, blob?: Blob | File): void {
    this.mediaUIDs.add(uid);

    if (blob) {
      this.cache[uid] = blob;
      this.sizes.set(uid, blob.size);

      // Persist without blocking the caller. Durability is best-effort: if
      // IndexedDB is unavailable or full the file still works this session,
      // which is exactly the behaviour before it was persisted at all.
      putMedia(uid, blob).catch((err) =>
        console.warn(`Could not persist ${uid}: ${String(err)}`),
      );
    }

    this.saveStateToStorageCache();
  }

  public static setCacheData(uid: string, blob: Blob | File): void {
    const mediaLookup = MediaLookup.getInstance();
    mediaLookup.mediaUIDs.add(uid);
    mediaLookup.cache[uid] = blob;
    mediaLookup.sizes.set(uid, blob.size);
    putMedia(uid, blob).catch((err) =>
      console.warn(`Could not persist ${uid}: ${String(err)}`),
    );
  }

  static getInstance(): MediaLookup {
    if (!MediaLookup.instance) {
      MediaLookup.instance = new MediaLookup();
    }
    return MediaLookup.instance;
  }

  public static hasAnyMedia(): boolean {
    const mediaLookup = MediaLookup.getInstance();
    return (
      mediaLookup.mediaUIDs.size > 0 ||
      Object.keys(mediaLookup.cache).length > 0 ||
      mediaLookup.sizes.size > 0
    );
  }

  /**
   * Whether a failed request means "no server there" rather than "the server
   * said no".
   *
   * fetch rejects only on a network-level failure; an HTTP error still
   * resolves. The distinction matters: an unreachable backend should fall back
   * to browser storage, while a backend that actively refused a file (too
   * large, wrong type) must surface that refusal rather than quietly storing
   * something the server rejected.
   */
  private static isBackendUnreachable(err: unknown): boolean {
    return err instanceof TypeError;
  }

  /** Logged at most once, so a backend-less session is not spammed. */
  private static warnedNoBackend = false;

  /** Uploads via the Flask backend. Rejects if it is unreachable. */
  private static uploadToBackend(file: File | Blob): Promise<string> {
    return MediaLookup.uploadLimiter.schedule(async () => {
      const formData = new FormData();
      formData.append("file", file);

      const res = await fetch(`${FLASK_BASE_URL}upload`, {
        method: "POST",
        body: formData,
      });

      if (!res.ok) throw new Error(`Upload failed: ${res.statusText}`);

      const json = await res.json();
      const uid = json.uid;

      if (!uid) throw new Error("Upload failed: No UID returned");

      // Store the UID in the media UIDs set
      MediaLookup.getInstance().add(uid);

      return uid;
    });
  }

  /** Keeps the file in the browser, under the client-side storage budget. */
  private static uploadToBrowser(file: File | Blob): string {
    // The browser holds the file itself, so enforce a budget before taking it
    // (throws with a message the caller can surface).
    const mediaLookup = MediaLookup.getInstance();
    mediaLookup.assertBrowserCacheHasRoom(
      file,
      "name" in file ? file.name : undefined,
    );

    // Make a uid for the file, and use it to cache the file:
    // NOTE: We keep the file name around, if there is one, just in case we need to recover it later.
    const uid =
      `cache__${uuid()}__cache` + ("name" in file ? `__${file.name}` : "");
    mediaLookup.add(uid, file);
    // Work is now saved in the browser, so ask it not to clear the storage.
    requestStorageProtection();
    return uid;
  }

  /**
   * Stores a file and returns its UID, using the backend when one is there.
   *
   * APP_IS_RUNNING_LOCALLY only reports whether the page is served from
   * localhost, which is not the same question as whether a Flask server is
   * answering. Serving the static build locally -- an ordinary way to run the
   * browser-only feature set -- put us on the backend path with no backend,
   * and the upload failed outright instead of falling back. So the hostname
   * is treated as a hint about where to try first, and an unreachable server
   * simply means the browser keeps the file.
   *
   * @param file A Blob or File object to upload
   * @returns The UID assigned by the backend, or a client-side UID
   */
  public static async upload(file: File | Blob): Promise<string> {
    if (APP_IS_RUNNING_LOCALLY()) {
      try {
        return await MediaLookup.uploadToBackend(file);
      } catch (err) {
        // A server that answered and refused is a real error; report it.
        if (!MediaLookup.isBackendUnreachable(err)) throw err;
        if (!MediaLookup.warnedNoBackend) {
          MediaLookup.warnedNoBackend = true;
          console.warn(
            "No ChainForge server is answering, so uploaded files will be " +
              "kept in this browser instead.",
          );
        }
      }
    }
    return MediaLookup.uploadToBrowser(file);
  }

  public static async uploadDataURL(dataURL: string): Promise<string> {
    const blob = dataURLToBlob(dataURL);
    const uid = await MediaLookup.upload(blob);
    return uid;
  }

  /**
   * Uids of files held only in memory for this session, via keepForSession.
   * They are not written to IndexedDB, so they are gone after a reload.
   */
  private sessionOnlyUIDs = new Set<string>();

  /**
   * Holds a file in memory for this session only, bypassing the storage
   * budget and IndexedDB. For content that can't be recreated for free, like a
   * generated image, when upload() refuses it because storage is full: losing
   * it on reload is better than discarding it now, or inlining its bytes into
   * the response cache, which would then no longer fit in localStorage and
   * stop the whole flow from autosaving.
   *
   * Callers should tell the user; see sessionOnlyCount.
   * @returns The uid of the file
   */
  public static keepForSession(file: File | Blob): string {
    const mediaLookup = MediaLookup.getInstance();
    const uid = `cache__${uuid()}__cache`;
    mediaLookup.mediaUIDs.add(uid);
    mediaLookup.cache[uid] = file;
    mediaLookup.sizes.set(uid, file.size);
    mediaLookup.sessionOnlyUIDs.add(uid);
    mediaLookup.saveStateToStorageCache();
    return uid;
  }

  /** keepForSession, for a file given as a data URL. */
  public static keepDataURLForSession(dataURL: string): string {
    return MediaLookup.keepForSession(dataURLToBlob(dataURL));
  }

  /** How many files are currently held for this session only. */
  public static sessionOnlyCount(): number {
    return MediaLookup.getInstance().sessionOnlyUIDs.size;
  }

  /**
   * Checks if a media UID exists in the lookup table.
   * @param uid The UID to check
   * @returns True if the UID exists, false otherwise
   */
  public static async has(uid: string): Promise<boolean> {
    const mediaLookup = MediaLookup.getInstance();
    const isInLookup = mediaLookup.mediaUIDs.has(uid);
    if (isInLookup) return true;
    else {
      console.error(
        `File with UID ${uid} not found in media UIDs. Still proceeding to try to fetch....`,
      );
    }
    const res = await fetch(`${FLASK_BASE_URL}mediaExists/${uid}`);
    if (res.ok) mediaLookup.add(uid); // Add to the mediaUIDs set if it exists
    return res.ok;
  }

  /**
   * Fetches the raw Blob from the backend using a UID.
   * @param uid The UID of the file
   * @returns Blob (image, PDF, etc.)
   */
  public static async get(uid: string): Promise<Blob | undefined> {
    // Check if the UID is a data URL in disguise,
    // in which case we convert it to a Blob and return it directly.
    if (uid.startsWith("data:")) {
      const blob = dataURLToBlob(uid);
      return blob;
    } else if (uid && uid.length > 512) {
      // If the UID is too long, it might be a base64 string content.
      // This can happen when loading files from older versions of ChainForge.
      try {
        return base64ToBlob(uid);
      } catch (error) {
        console.error(
          `Error converting base64 string to Blob: ${(error as Error).message}`,
        );
        return undefined;
      }
    }

    // Check if the file is in the mediaUIDs
    const mediaLookup = MediaLookup.getInstance();
    const isInLookup = mediaLookup.mediaUIDs.has(uid);
    if (!isInLookup) {
      console.warn(
        `File with UID ${uid} not found in media UIDs. Still proceeding to try to fetch....`,
      );
    }

    // Check if the file is in the temp cache first
    const tempCachedBlob = mediaLookup.getFromTempCache(uid);
    if (tempCachedBlob) {
      return tempCachedBlob;
    }

    if (APP_IS_RUNNING_LOCALLY() && !uid.startsWith("cache__")) {
      // Fetch the file from the backend
      // NOTE: We use the limiter to throttle lookups, so we don't overload the server.
      return MediaLookup.lookupLimiter.schedule(async () => {
        try {
          const res = await fetch(`${FLASK_BASE_URL}media/${uid}`);
          if (!res.ok) {
            console.error(`Fetch failed for UID ${uid}: ${res.statusText}`);
            return undefined;
          }

          const blob = await res.blob();

          // If the file was for some reason not listed in the media UIDs, add it now:
          if (!isInLookup) mediaLookup.add(uid);

          // Add to the temp cache
          mediaLookup.addToTempCache(uid, blob);

          return blob;
        } catch (error) {
          if (MediaLookup.isBackendUnreachable(error))
            // Nothing is listening; the file may still be held locally.
            return await MediaLookup.getFromBrowser(uid, isInLookup);
          console.error(
            `Error fetching file with UID ${uid}: ${(error as Error).message}`,
          );
          return undefined;
        }
      });
    }
    return MediaLookup.getFromBrowser(uid, isInLookup);
  }

  /** Looks a file up in browser storage, in memory then IndexedDB. */
  private static async getFromBrowser(
    uid: string,
    isInLookup: boolean,
  ): Promise<Blob | undefined> {
    const mediaLookup = MediaLookup.getInstance();

    // Check if the file is in the cache
    let blob = mediaLookup.cache[uid];

    // Not in memory: it may have been persisted in an earlier session.
    // Loading on demand keeps memory proportional to what's actually used.
    if (!blob) {
      const persisted = await getMedia(uid);
      if (persisted) {
        blob = persisted;
        mediaLookup.cache[uid] = persisted;
        mediaLookup.sizes.set(uid, persisted.size);
      }
    }

    if (blob) {
      // If the file was for some reason not listed in the media UIDs, add it now:
      if (!isInLookup) mediaLookup.add(uid);

      return blob;
    }
    console.error(`File with UID ${uid} not found in cache.`);
    return undefined;
  }

  /**
   * Fetches the text content from the backend using a UID.
   * @param uid The UID of the file (must be a doc)
   * @returns Text content as a string
   */
  public static async getAsText(uid: string): Promise<string | undefined> {
    // Check if the file is in the mediaUIDs
    const mediaLookup = MediaLookup.getInstance();
    const isInLookup = mediaLookup.mediaUIDs.has(uid);
    if (!isInLookup) {
      console.error(
        `File with UID ${uid} not found in media UIDs. Still proceeding to try to fetch....`,
      );
    }

    // As in upload(): being served from localhost is not proof that a server
    // is answering, so an unreachable one falls through to doing the
    // extraction here rather than failing the upload.
    if (APP_IS_RUNNING_LOCALLY() && !uid.startsWith("cache__")) {
      try {
        const res = await fetch(`${FLASK_BASE_URL}mediaToText/${uid}`);
        if (!res.ok) {
          throw new Error(`Fetch failed for UID ${uid}: ${res.statusText}`);
        }
        const json = await res.json();
        const text = json.text;

        if (text === undefined)
          throw new Error(`Fetch failed for UID ${uid}: No text returned`);

        // If the file was for some reason not listed in the media UIDs, add it now:
        if (!isInLookup) mediaLookup.add(uid);

        return json.text;
      } catch (err) {
        // A server that answered and refused is a real error.
        if (!MediaLookup.isBackendUnreachable(err)) throw err;
      }
    }

    // No backend to convert the file, so do it client-side. Formats needing
    // a real parser (PDF, DOCX) throw with an explanation.
    const blob = await MediaLookup.get(uid);
    if (!blob)
      throw new Error(
        `No file contents are available for UID ${uid}. It may have been ` +
          `uploaded in a previous session -- file contents are held in ` +
          `memory only when running without a local server.`,
      );

    if (!isInLookup) mediaLookup.add(uid);
    return await extractTextInBrowser(blob, uid);
  }

  /**
   * Returns a new object URL (e.g., for <img src=...>) for a given UID.
   * The caller owns the URL and must revoke it with URL.revokeObjectURL, or it
   * keeps the file's bytes alive for the rest of the session. Components should
   * prefer the `useMediaUrl` hook, which manages this through acquireUrl.
   * @param uid The UID of the file
   * @returns An object URL string
   */
  public static async getUrl(uid: string): Promise<string | undefined> {
    const blob = await MediaLookup.get(uid);
    if (!blob) {
      console.error(`Blob not found for UID ${uid}`);
      return undefined;
    }
    return URL.createObjectURL(blob);
  }

  /**
   * Returns an object URL for a UID, shared with every other holder of the same
   * UID. Each call must be balanced by one releaseUrl(uid); the URL is revoked
   * when the last holder releases it.
   * @param uid The UID of the file
   * @returns An object URL string, or undefined if the file is unavailable
   */
  public static async acquireUrl(uid: string): Promise<string | undefined> {
    const mediaLookup = MediaLookup.getInstance();
    const existing = mediaLookup.objectUrls.get(uid);
    if (existing) {
      existing.refs++;
      return existing.url;
    }

    const blob = await MediaLookup.get(uid);
    if (!blob) {
      console.error(`Blob not found for UID ${uid}`);
      return undefined;
    }

    // Another caller may have created one while the blob was loading.
    const raced = mediaLookup.objectUrls.get(uid);
    if (raced) {
      raced.refs++;
      return raced.url;
    }

    const url = URL.createObjectURL(blob);
    mediaLookup.objectUrls.set(uid, { url, refs: 1 });
    return url;
  }

  /** Releases one hold on a URL from acquireUrl, revoking it after the last. */
  public static releaseUrl(uid: string): void {
    const mediaLookup = MediaLookup.getInstance();
    const entry = mediaLookup.objectUrls.get(uid);
    if (!entry) return;
    entry.refs--;
    if (entry.refs <= 0) mediaLookup.revokeObjectUrl(uid);
  }

  /**
   * Clears the media lookup table and its cache entirely.
   */
  public static clear(): void {
    const mediaLookup = MediaLookup.getInstance();
    mediaLookup.mediaUIDs.clear();
    mediaLookup.cache = {};
    mediaLookup.sizes.clear();
    clearMedia().catch(() => undefined);
    mediaLookup.clearCount++;
    mediaLookup.sessionOnlyUIDs.clear();
    for (const uid of Array.from(mediaLookup.objectUrls.keys()))
      mediaLookup.revokeObjectUrl(uid);

    // Clear the temp cache
    mediaLookup.tempCache.items.clear();
    mediaLookup.tempCache.size = 0;
    mediaLookup.tempCache.accessOrder = [];

    mediaLookup.saveStateToStorageCache();
  }

  /**
   * Removes a specific UID from the media lookup table and its cache.
   * @param uid The UID to remove
   */
  public static remove(uid: string): void {
    const mediaLookup = MediaLookup.getInstance();
    mediaLookup.mediaUIDs.delete(uid);
    delete mediaLookup.cache[uid];
    mediaLookup.sizes.delete(uid);
    deleteMedia(uid).catch(() => undefined);
    mediaLookup.sessionOnlyUIDs.delete(uid);
    mediaLookup.revokeObjectUrl(uid);

    // Remove from temp cache if present
    const cachedBlob = mediaLookup.tempCache.items.get(uid);
    if (cachedBlob) {
      mediaLookup.tempCache.size -= cachedBlob.size;
      mediaLookup.tempCache.items.delete(uid);
      mediaLookup.tempCache.accessOrder =
        mediaLookup.tempCache.accessOrder.filter((id) => id !== uid);
    }

    mediaLookup.saveStateToStorageCache();
  }

  /**
   * Restores the media lookup table from a saved state.
   * NOTE: This deliberately does not override the current mediaUIDs or cache.
   * If you want to clear the current mediaUIDs or cache, use the clear() method first.
   * @param savedState The saved state containing mediaUIDs and cache.
   */
  public static restoreFrom(savedState?: {
    uids: string[];
    cache?: Dict<string>;
  }): void {
    if (!savedState) return;

    const mediaLookup = MediaLookup.getInstance();

    if (Array.isArray(savedState.uids)) {
      savedState.uids.forEach((uid) => {
        mediaLookup.mediaUIDs.add(uid);
      });
    }

    if (savedState.cache !== undefined) {
      // Convert each data URL back to a Blob
      for (const [key, base64String] of Object.entries(savedState.cache)) {
        const blob = dataURLToBlob(base64String);
        mediaLookup.cache[key] = blob;
        mediaLookup.sizes.set(key, blob.size);
        // Persist too, so files that arrive by importing a flow survive a
        // reload like uploaded ones do.
        putMedia(key, blob).catch(() => undefined);
      }
    }

    mediaLookup.saveStateToStorageCache();
  }

  /**
   * Serializes the media lookup table to a JSON-compatible object.
   * @returns An object containing the mediaUIDs and cache.
   */
  public static async toJSON(): Promise<{
    uids: string[];
    cache?: Dict<string>;
  }> {
    const mediaLookup = MediaLookup.getInstance();

    // Convert each Blob to a base64 string
    const serializedCache: Dict<Promise<string>> = {};
    for (const [key, blob] of Object.entries(mediaLookup.cache)) {
      serializedCache[key] = blobOrFileToDataURL(blob);
    }

    // Wait for all the base64 strings to be generated
    const serializedCacheEntries = await Promise.all(
      Object.entries(serializedCache).map(async ([key, blobPromise]) => {
        const base64String = await blobPromise;
        return [key, base64String];
      }),
    );

    // Convert the array of entries back to an object
    const serializedBlobs: Dict<string> = Object.fromEntries(
      serializedCacheEntries,
    );

    // Files persisted by an earlier session are indexed in `sizes` but loaded
    // into `cache` only on demand, so a file not viewed since the last reload
    // has no bytes in memory. Read those from IndexedDB, or the export would
    // carry their uids without their contents. They are not kept in memory.
    for (const uid of Array.from(mediaLookup.sizes.keys())) {
      if (uid in serializedBlobs) continue;
      const blob = await getMedia(uid);
      if (blob) serializedBlobs[uid] = await blobOrFileToDataURL(blob);
    }

    return {
      uids: Array.from(mediaLookup.mediaUIDs),
      cache: serializedBlobs,
    };
  }
}
